import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type HTMLAttributes } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Archive, ArrowDown, ArrowUp, ArrowUpDown, Building2, ExternalLink, ListPlus, Pencil, RotateCcw } from 'lucide-react'
import clsx from 'clsx'

import { crmErrorDetail, crmService, type CrmContact } from '@/services/crm'
import type { RelationshipRow } from '@/services/crmRelationships'
import {
  contactInitials,
  NEEDS_REVIEW_TITLE,
  NOT_ON_RECORD,
  scoreBg,
  scoreColor,
  sortContacts,
  typeBadgeColor,
  type ContactSortField,
  type SortDir,
} from './crmFormat'
import { CONTACT_COLUMNS, DEFAULT_CONTACT_COLUMNS, normalizeContactColumns, useColumnReorder, type ContactColumn, type ContactColumnKey } from './contactColumns'
import { ActionMenu, MenuConfirm, MenuItem } from './ActionMenu'
import type { ContactDensity } from './contactDensity'
import AddToListPopover from './AddToListPopover'
import Sparkline from './relationships/Sparkline'
import { BAND_STYLE, STAGE_STYLE, healthDescription } from './relationships/relationshipFormat'



/**
 * The directory as a table: one row per contact, every column sortable, a
 * single click to read, a double click (or Enter) to open the whole file.
 *
 * Rows are real `<tr>` elements with a tab stop, so the arrow keys walk the
 * list and a screen reader announces a table, not a pile of divs.
 */
export default function ContactTable({
  contacts,
  selectedId,
  onSelect,
  onOpen,
  onEdit,
  onRetired,
  loading = false,
  emptyMessage,
  className,
  columns,
  onColumnsChange,
  density = 'compact',
  relationships,
}: {
  contacts: CrmContact[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  onOpen: (id: string) => void
  onEdit: (contact: CrmContact) => void
  /** After a retire or restore landed, so the page can drop a selection that
   *  no longer points at a visible row. */
  onRetired?: (id: string) => void
  loading?: boolean
  /** What to say when the filter selects nobody. */
  emptyMessage?: string
  className?: string
  /** Visible columns in the user's saved order. Name remains visible. */
  columns?: ContactColumnKey[]
  onColumnsChange?: (next: ContactColumnKey[]) => void
  /** Row height. Compact reads a long list; roomy reads one row at a time. */
  density?: ContactDensity
  /** The server's relationship rows by contact id, for the Stage, Health and
   *  12-month columns. Without one a contact shows a dash in those columns. */
  relationships?: ReadonlyMap<string, RelationshipRow>
}) {
  const [sortField, setSortField] = useState<ContactSortField>('lastContact')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const bodyRef = useRef<HTMLTableSectionElement>(null)
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => {
    if (clickTimer.current) clearTimeout(clickTimer.current)
  }, [])

  const shown = useMemo<ContactColumn[]>(() => {
    return normalizeContactColumns(columns ?? DEFAULT_CONTACT_COLUMNS).map((key) => CONTACT_COLUMNS.find((c) => c.key === key)!)
  }, [columns])

  const reorder = useColumnReorder(shown.map((c) => c.key), onColumnsChange)

  // Every visible column gets a share of the width the table actually has,
  // so the whole set always fits and nothing ever scrolls sideways. The
  // shares are proportional to how much a column's content usually needs;
  // a value that still does not fit is clipped with an ellipsis and carried
  // in full on the cell's title.
  const widths = useMemo(() => columnWidths(shown.map((c) => c.key)), [shown])

  const sorted = useMemo(
    () => sortContacts(contacts, sortField, sortDir, relationships),
    [contacts, sortField, sortDir, relationships],
  )

  function toggleSort(field: ContactSortField) {
    if (sortField === field) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortField(field)
    // Dates and counts read newest/most first; words read A to Z.
    setSortDir(field === 'name' || field === 'company' || field === 'type' || field === 'stage' ? 'asc' : 'desc')
  }

  // Rows are found by walking the data attribute rather than by a selector,
  // because an id is not a safe selector literal and jsdom has no CSS.escape.
  const rowFor = useCallback((id: string): HTMLElement | undefined => {
    return Array.from(bodyRef.current?.querySelectorAll<HTMLElement>('tr[data-contact-id]') ?? []).find(
      (row) => row.dataset.contactId === id,
    )
  }, [])

  // A selection made elsewhere — a deep link, a merge that moved to the
  // survivor — scrolls its row into view. jsdom has no scrollIntoView.
  useEffect(() => {
    if (!selectedId) return
    rowFor(selectedId)?.scrollIntoView?.({ block: 'nearest' })
  }, [selectedId, rowFor])

  function move(from: string, delta: 1 | -1) {
    const index = sorted.findIndex((c) => c.id === from)
    const next = sorted[index + delta]
    if (!next) return
    onSelect(next.id)
    rowFor(next.id)?.focus()
  }

  function onRowKey(event: KeyboardEvent<HTMLTableRowElement>, contact: CrmContact) {
    switch (event.key) {
      case 'Enter':
        event.preventDefault()
        onSelect(contact.id)
        onOpen(contact.id)
        break
      case ' ':
        event.preventDefault()
        onSelect(selectedId === contact.id ? null : contact.id)
        break
      case 'ArrowDown':
        event.preventDefault()
        move(contact.id, 1)
        break
      case 'ArrowUp':
        event.preventDefault()
        move(contact.id, -1)
        break
      default:
        break
    }
  }

  function onRowClick(event: MouseEvent<HTMLTableRowElement>, contact: CrmContact) {
    // The second click of a double click arrives as a click with detail 2;
    // acting on it would deselect the row an instant before the double click
    // opens it, and the panel would blink.
    if (event.detail > 1) return
    // Selecting here reflows the table (the profile panel claims ~42% of
    // the width), so acting immediately can move the row out from under a
    // double click's second click before it lands. Give a double click the
    // OS's own timing window to cancel this first.
    if (clickTimer.current) clearTimeout(clickTimer.current)
    clickTimer.current = setTimeout(() => {
      clickTimer.current = null
      onSelect(selectedId === contact.id ? null : contact.id)
    }, 250)
  }

  function onRowDoubleClick(contact: CrmContact) {
    if (clickTimer.current) {
      clearTimeout(clickTimer.current)
      clickTimer.current = null
    }
    onSelect(contact.id)
    onOpen(contact.id)
  }

  return (
    <div className={clsx('min-h-0 flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin', className)}>
      {/* Fixed layout: the visible columns share the width the table has, in
          proportions set per column (`columnWidths`), so the directory fits
          whatever the viewport is and never scrolls sideways — beside the
          profile panel included. A value wider than its share is clipped
          with an ellipsis and carried in full on the cell's title, rather
          than pushing the table past the edge of the screen.

          Everything is centre-aligned, headers and cells alike. Row height
          is the reader's choice: compact for scanning, roomy for reading. */}
      <table
        data-density={density}
        className={clsx(
          'dense-table w-full table-fixed [&_td]:!px-2 [&_th]:!px-2 [&_td]:text-center [&_th]:text-center',
          density === 'roomy' ? '[&_td]:!py-2.5' : '[&_td]:!py-1',
        )}
      >
        <colgroup>
          {shown.map((column) => (
            <col key={column.key} style={{ width: `${widths[column.key]}%` }} />
          ))}
          <col style={{ width: 32 }} />
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr>
            {shown.map((column) =>
              column.sort ? (
                <SortHeader
                  key={column.key}
                  label={column.label}
                  field={column.sort}
                  align={column.key === 'name' ? 'left' : 'center'}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={toggleSort}
                  dragProps={reorder.props(column.key)}
                  dropTarget={reorder.target === column.key}
                  columnKey={column.key}
                />
              ) : (
                <th key={column.key} {...reorder.props(column.key)} data-column-key={column.key} title={column.label} className={clsx("select-none truncate text-center", onColumnsChange && "cursor-grab", reorder.target === column.key && "bg-amber-400/15 ring-1 ring-inset ring-amber-300/50")}>
                  <span className="uppercase tracking-[0.16em]">{column.label}</span>
                </th>
              ),
            )}
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {loading
            ? Array.from({ length: 6 }, (_, i) => (
                <tr key={`skeleton-${i}`} aria-hidden="true" data-testid="contact-skeleton">
                  {Array.from({ length: shown.length }, (_, cell) => (
                    <td key={cell}>
                      <div className="mx-auto h-3 w-[70%] animate-pulse rounded bg-stone-800/70 motion-reduce:animate-none" />
                    </td>
                  ))}
                  <td />
                </tr>
              ))
            : null}
          {!loading && sorted.length === 0 ? (
            <tr>
              <td colSpan={shown.length + 1} className="py-8 text-center text-[11px] italic text-stone-500">
                {emptyMessage ?? 'No contacts in view.'}
              </td>
            </tr>
          ) : null}
          {!loading
            ? sorted.map((contact) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  columns={shown}
                  relationship={relationships?.get(contact.id)}
                  selected={selectedId === contact.id}
                  onClick={(event) => onRowClick(event, contact)}
                  onDoubleClick={() => onRowDoubleClick(contact)}
                  onKeyDown={(event) => onRowKey(event, contact)}
                  onOpen={() => onOpen(contact.id)}
                  onEdit={() => onEdit(contact)}
                  onRetired={onRetired}
                />
              ))
            : null}
        </tbody>
      </table>
    </div>
  )
}

/** A sortable column heading. The sorted column shows its direction; the rest
 *  show the glyph under the pointer only, so a wide column set does not carry
 *  an icon's width per header. */
function SortHeader({
  label,
  field,
  align,
  sortField,
  sortDir,
  onSort,
  dragProps,
  dropTarget,
  columnKey,
}: {
  label: string
  field: ContactSortField
  align: 'left' | 'center'
  sortField: ContactSortField
  sortDir: SortDir
  onSort: (field: ContactSortField) => void
  dragProps?: HTMLAttributes<HTMLElement>
  dropTarget?: boolean
  columnKey?: ContactColumnKey
}) {
  const active = sortField === field
  return (
    <th
      {...dragProps}
      data-column-key={columnKey}
      title={dragProps?.title ? `${label} — ${dragProps.title}` : label}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={clsx('group select-none truncate', dragProps?.draggable && 'cursor-grab', dropTarget && 'bg-amber-400/15 ring-1 ring-inset ring-amber-300/50', align === 'left' ? '!text-left' : 'text-center')}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={clsx(
          'relative inline-flex items-center gap-1 rounded uppercase tracking-[0.16em] hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
          align === 'center' && 'mx-auto justify-center',
          active && 'text-amber-300',
        )}
      >
        {label}
        {active ? (
          sortDir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />
        ) : (
          <ArrowUpDown
            size={9}
            className="absolute left-full top-1/2 ml-1 -translate-y-1/2 opacity-0 group-hover:opacity-60"
            aria-hidden="true"
          />
        )}
      </button>
    </th>
  )
}

function ContactRow({
  contact,
  columns,
  relationship,
  selected,
  onClick,
  onDoubleClick,
  onKeyDown,
  onOpen,
  onEdit,
  onRetired,
}: {
  contact: CrmContact
  columns: ContactColumn[]
  relationship?: RelationshipRow
  selected: boolean
  onClick: (event: MouseEvent<HTMLTableRowElement>) => void
  onDoubleClick: () => void
  onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => void
  onOpen: () => void
  onEdit: () => void
  onRetired?: (id: string) => void
}) {
  const retired = Boolean(contact.retired)
  const entity = contact.kind === 'organisation' || contact.isEntity
  return (
    <tr
      data-contact-id={contact.id}
      tabIndex={0}
      aria-selected={selected}
      title="Double-click to open the full contact"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
      className={clsx(
        'cursor-pointer focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-amber-400/60',
        selected && 'selected',
        retired && 'opacity-70',
      )}
    >
      {columns.map((column) => column.key === 'name' ? (
      <td key="name" className="!text-left" title={contact.displayName}>
        <div className="flex min-w-0 items-center gap-2">
          {/* An organisation is drawn as one: a square with a building, not a
              person's initials in a person's round badge. */}
          <div
            className={clsx(
              'flex h-6 w-6 flex-shrink-0 items-center justify-center border border-amber-400/15 bg-amber-400/[0.06] text-[9px] font-bold text-amber-300',
              entity ? 'rounded' : 'rounded-full',
            )}
            title={entity ? 'An organization' : 'A person'}
            aria-hidden="true"
          >
            {entity ? <Building2 size={11} /> : contactInitials(contact)}
          </div>
          <div className="min-w-0 text-left">
            <div className="flex min-w-0 items-center gap-1.5">
              <span
                className={clsx(
                  'truncate text-[12px] font-medium',
                  retired ? 'text-stone-500 line-through' : 'text-stone-200',
                )}
              >
                {contact.displayName}
              </span>
              {contact.needsReview && !retired ? (
                <span
                  className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400"
                  title={NEEDS_REVIEW_TITLE}
                  role="img"
                  aria-label={NEEDS_REVIEW_TITLE}
                />
              ) : null}
              {retired ? (
                <span className="flex-shrink-0 rounded border border-stone-600/40 px-1 text-[8px] uppercase tracking-wider text-stone-500">
                  Retired
                </span>
              ) : null}
            </div>
            <div className="truncate text-[10px] text-stone-500">{contact.title ?? NOT_ON_RECORD}</div>
          </div>
        </div>
      </td>
      ) : <ValueCell key={column.key} column={column} contact={contact} relationship={relationship} />)}
      <td className="!px-0 text-center">
        <RowMenu contact={contact} onOpen={onOpen} onEdit={onEdit} onRetired={onRetired} />
      </td>
    </tr>
  )
}

/** One column's cell. Type and Score keep their badges; everything else is the
 *  registry's own string, on one line so the column asks for the width its
 *  content actually needs. */
function ValueCell({
  column,
  contact,
  relationship,
}: {
  column: ContactColumn
  contact: CrmContact
  relationship?: RelationshipRow
}) {
  if (column.key === 'stage') {
    const stage = relationship?.stage ?? null
    const style = stage ? STAGE_STYLE[stage] : null
    const title = relationship?.reason ?? (relationship?.staff ? 'Desk staff are not staged' : 'No stage: nothing dated on record')
    return (
      <td className="whitespace-nowrap" title={title}>
        {style ? (
          <span className={clsx('inline-flex items-center gap-1.5 text-[11px]', style.text)}>
            <span className={clsx('h-1.5 w-1.5 rounded-full', style.dot)} aria-hidden="true" />
            {style.title}
          </span>
        ) : (
          <span className="text-stone-600">—</span>
        )}
      </td>
    )
  }
  if (column.key === 'health') {
    const band = relationship?.healthBand ?? null
    return (
      <td className="whitespace-nowrap" title={healthDescription(relationship)}>
        {relationship && relationship.health !== null ? (
          <span
            className={clsx(
              'inline-flex h-5 min-w-[2rem] items-center justify-center rounded-full border border-white/10 px-1.5 text-[10px] font-bold',
              band ? BAND_STYLE[band].text : 'text-stone-400',
            )}
          >
            {relationship.health}
          </span>
        ) : relationship?.stage === 'new' ? (
          <span className="text-[10px] font-medium uppercase tracking-wider text-sky-300">New</span>
        ) : (
          <span className="text-stone-600">—</span>
        )}
      </td>
    )
  }
  if (column.key === 'activity') {
    return (
      <td className="whitespace-nowrap">
        {relationship ? (
          <Sparkline values={relationship.spark} tone={relationship.stage === 'cooling' ? 'cooling' : 'neutral'} compact className="mx-auto justify-center" />
        ) : (
          <span className="text-stone-600">—</span>
        )}
      </td>
    )
  }
  if (column.key === 'type') {
    return (
      <td className="whitespace-nowrap">
        <span
          title={contact.companyType}
          className={clsx(
            'inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-full border px-1.5 py-0.5 align-middle text-[10px] font-medium',
            typeBadgeColor(contact.companyType),
          )}
        >
          {contact.companyType}
        </span>
      </td>
    )
  }
  if (column.key === 'score') {
    return (
      <td className="whitespace-nowrap">
        <span
          className={clsx(
            'inline-flex h-5 w-8 items-center justify-center rounded border text-[10px] font-bold',
            scoreBg(contact.score),
            scoreColor(contact.score),
          )}
        >
          {contact.score ?? '—'}
        </span>
      </td>
    )
  }
  const value = column.value(contact)
  return (
    <td
      className={clsx(
        'truncate',
        column.key === 'deals' ? 'font-mono text-stone-300' : 'text-stone-300',
        column.key === 'lastContact' && 'text-stone-400',
      )}
      title={value}
    >
      {value}
    </td>
  )
}

/** How much of the table each column asks for, relative to the others. The
 *  numbers are proportions, not pixels: `columnWidths` scales whichever set
 *  is visible to exactly 100%, so adding or hiding a column re-shares the
 *  same width rather than pushing the table past the edge. */
const COLUMN_WEIGHT: Record<ContactColumnKey, number> = {
  name: 20,
  company: 15,
  type: 10,
  stage: 11,
  health: 8,
  deals: 7,
  activity: 8,
  lastContact: 11,
  score: 6,
  title: 13,
  email: 17,
  phone: 11,
  mobile: 11,
  address: 18,
  city: 10,
  state: 6,
  linkedin: 13,
}

export function columnWidths(keys: ContactColumnKey[]): Record<string, number> {
  const total = keys.reduce((sum, key) => sum + COLUMN_WEIGHT[key], 0) || 1
  const out: Record<string, number> = {}
  for (const key of keys) out[key] = Math.round((COLUMN_WEIGHT[key] / total) * 1000) / 10
  return out
}

/** The row's ⋯: Open, Edit, Add to list, Retire — or Restore for a retired row. */
function RowMenu({
  contact,
  onOpen,
  onEdit,
  onRetired,
}: {
  contact: CrmContact
  onOpen: () => void
  onEdit: () => void
  onRetired?: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<'items' | 'lists' | 'retire'>('items')
  const [error, setError] = useState<string | null>(null)

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
    ])

  const retire = useMutation({
    mutationFn: () => crmService.archiveContact(contact.id),
    onSuccess: async () => {
      await refresh()
      onRetired?.(contact.id)
    },
    onError: (failure) => setError(crmErrorDetail(failure, 'The contact could not be retired.')),
  })
  const restore = useMutation({
    mutationFn: () => crmService.restoreContact(contact.id),
    onSuccess: async () => {
      await refresh()
      onRetired?.(contact.id)
    },
    onError: (failure) => setError(crmErrorDetail(failure, 'The contact could not be restored.')),
  })

  return (
    <ActionMenu label={`Actions for ${contact.displayName}`}>
      {(close) => {
        const done = () => {
          setMode('items')
          setError(null)
          close()
        }
        if (mode === 'lists') {
          return <AddToListPopover contactId={contact.id} contactName={contact.displayName} />
        }
        if (mode === 'retire') {
          return (
            <MenuConfirm
              question={`Retire ${contact.displayName}?`}
              confirmLabel="Yes, retire"
              pending={retire.isPending}
              error={error}
              onConfirm={() => retire.mutate(undefined, { onSuccess: done })}
              onCancel={() => {
                setMode('items')
                setError(null)
              }}
            />
          )
        }
        return (
          <>
            <MenuItem icon={<ExternalLink size={11} />} onSelect={() => { done(); onOpen() }}>
              Open
            </MenuItem>
            <MenuItem icon={<Pencil size={11} />} onSelect={() => { done(); onEdit() }}>
              Edit
            </MenuItem>
            <MenuItem icon={<ListPlus size={11} />} onSelect={() => setMode('lists')}>
              Add to list…
            </MenuItem>
            {contact.retired ? (
              <MenuItem
                icon={<RotateCcw size={11} />}
                disabled={restore.isPending}
                onSelect={() => restore.mutate(undefined, { onSuccess: done })}
              >
                {restore.isPending ? 'Restoring…' : 'Restore'}
              </MenuItem>
            ) : (
              <MenuItem icon={<Archive size={11} />} danger onSelect={() => setMode('retire')}>
                Retire…
              </MenuItem>
            )}
            {error ? <div className="px-2 py-1 text-[10px] text-red-300">{error}</div> : null}
          </>
        )
      }}
    </ActionMenu>
  )
}
